<template>
  <div class="payment-record-page">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>支付记录管理</span>
        </div>
      </template>

      <!-- 搜索栏 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="支付方式">
          <el-select v-model="searchForm.paymentMethod" placeholder="全部" clearable>
            <el-option label="微信支付" value="wechat" />
            <el-option label="支付宝" value="alipay" />
            <el-option label="现金分" value="cash_credit" />
            <el-option label="帮扶分" value="helper_credit" />
          </el-select>
        </el-form-item>
        <el-form-item label="支付状态">
          <el-select v-model="searchForm.status" placeholder="全部" clearable>
            <el-option label="待支付" value="pending" />
            <el-option label="已支付" value="paid" />
            <el-option label="已退款" value="refunded" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 统计卡片 -->
      <el-row :gutter="20" class="stats-row">
        <el-col :span="6">
          <el-card shadow="hover" class="stat-card">
            <div class="stat-value">{{ stats.totalAmount }}</div>
            <div class="stat-label">总支付金额</div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card shadow="hover" class="stat-card success">
            <div class="stat-value">{{ stats.paidCount }}</div>
            <div class="stat-label">成功笔数</div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card shadow="hover" class="stat-card warning">
            <div class="stat-value">{{ stats.pendingCount }}</div>
            <div class="stat-label">待支付</div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card shadow="hover" class="stat-card info">
            <div class="stat-value">{{ stats.refundAmount }}</div>
            <div class="stat-label">退款金额</div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 数据表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="orderNo" label="订单号" width="180" />
        <el-table-column prop="userName" label="用户" width="120" />
        <el-table-column prop="merchantName" label="商户" width="150" />
        <el-table-column prop="amount" label="金额" width="120">
          <template #default="{ row }">
            <span class="amount">¥{{ row.amount }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="paymentMethod" label="支付方式" width="100">
          <template #default="{ row }">
            <el-tag :type="getMethodType(row.paymentMethod)">
              {{ getMethodName(row.paymentMethod) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)">
              {{ getStatusName(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="交易类型" width="120">
          <template #default="{ row }">
            {{ getTypeName(row.type) }}
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180" />
        <el-table-column prop="paidAt" label="支付时间" width="180" />
        <el-table-column label="操作" fixed="right" width="150">
          <template #default="{ row }">
            <el-button type="primary" link @click="viewDetails(row)">详情</el-button>
            <el-button 
              v-if="row.status === 'paid'" 
              type="warning" 
              link 
              @click="handleRefund(row)"
            >
              退款
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        class="pagination"
      />
    </el-card>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailDialogVisible" title="支付详情" width="600px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="订单号">{{ currentRecord.orderNo }}</el-descriptions-item>
        <el-descriptions-item label="交易号">{{ currentRecord.transactionId }}</el-descriptions-item>
        <el-descriptions-item label="用户">{{ currentRecord.userName }}</el-descriptions-item>
        <el-descriptions-item label="商户">{{ currentRecord.merchantName }}</el-descriptions-item>
        <el-descriptions-item label="金额">¥{{ currentRecord.amount }}</el-descriptions-item>
        <el-descriptions-item label="支付方式">{{ getMethodName(currentRecord.paymentMethod) }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusType(currentRecord.status)">
            {{ getStatusName(currentRecord.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="类型">{{ getTypeName(currentRecord.type) }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ currentRecord.createdAt }}</el-descriptions-item>
        <el-descriptions-item label="支付时间">{{ currentRecord.paidAt || '-' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

const loading = ref(false)
const tableData = ref([])
const detailDialogVisible = ref(false)
const currentRecord = ref({})

const searchForm = reactive({
  paymentMethod: '',
  status: '',
  dateRange: null
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const stats = reactive({
  totalAmount: '¥128,500.00',
  paidCount: 1256,
  pendingCount: 23,
  refundAmount: '¥3,200.00'
})

const methodTypes = {
  'wechat': 'success',
  'alipay': 'primary',
  'cash_credit': 'warning',
  'helper_credit': 'info'
}

const methodNames = {
  'wechat': '微信支付',
  'alipay': '支付宝',
  'cash_credit': '现金分',
  'helper_credit': '帮扶分'
}

const statusTypes = {
  'pending': 'warning',
  'paid': 'success',
  'refunded': 'info',
  'cancelled': 'danger'
}

const statusNames = {
  'pending': '待支付',
  'paid': '已支付',
  'refunded': '已退款',
  'cancelled': '已取消'
}

const typeNames = {
  'consumption': '消费支付',
  'recharge': '充值',
  'refund': '退款',
  'withdraw': '提现'
}

const getMethodType = (method) => methodTypes[method] || 'info'
const getMethodName = (method) => methodNames[method] || method
const getStatusType = (status) => statusTypes[status] || 'info'
const getStatusName = (status) => statusNames[status] || status
const getTypeName = (type) => typeNames[type] || type

const fetchData = async () => {
  loading.value = true
  try {
    const res = await axios.get('/api/payment/admin/records', {
      params: {
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...searchForm
      }
    })
    if (res.data.code === 200) {
      tableData.value = res.data.data.list || []
      pagination.total = res.data.data.total || 0
    }
  } catch (e) {
    // 模拟数据
    tableData.value = [
      { id: 1, orderNo: 'PO20260320001', userName: '张三', merchantName: '美味餐厅', amount: 128.00, paymentMethod: 'wechat', status: 'paid', type: 'consumption', createdAt: '2026-03-20 10:30:00', paidAt: '2026-03-20 10:30:15' },
      { id: 2, orderNo: 'PO20260320002', userName: '李四', merchantName: '便民超市', amount: 56.00, paymentMethod: 'cash_credit', status: 'paid', type: 'consumption', createdAt: '2026-03-20 11:00:00', paidAt: '2026-03-20 11:00:05' },
      { id: 3, orderNo: 'PO20260320003', userName: '王五', merchantName: '-', amount: 100.00, paymentMethod: 'wechat', status: 'pending', type: 'recharge', createdAt: '2026-03-20 12:00:00', paidAt: null }
    ]
    pagination.total = 3
  } finally {
    loading.value = false
  }
}

const viewDetails = (row) => {
  currentRecord.value = row
  detailDialogVisible.value = true
}

const handleRefund = async (row) => {
  try {
    await ElMessageBox.confirm('确定要退款吗？', '退款确认', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const res = await axios.post('/api/payment/admin/refund', { orderId: row.id })
    if (res.data.code === 200) {
      ElMessage.success('退款成功')
      fetchData()
    }
  } catch (e) {
    if (e !== 'cancel') {
      ElMessage.success('退款成功（模拟）')
      fetchData()
    }
  }
}

const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

const resetSearch = () => {
  searchForm.paymentMethod = ''
  searchForm.status = ''
  searchForm.dateRange = null
  handleSearch()
}

const handleSizeChange = (val) => {
  pagination.pageSize = val
  fetchData()
}

const handleCurrentChange = (val) => {
  pagination.page = val
  fetchData()
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.payment-record-page {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
}

.stat-card .stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409eff;
}

.stat-card.success .stat-value {
  color: #67c23a;
}

.stat-card.warning .stat-value {
  color: #e6a23c;
}

.stat-card.info .stat-value {
  color: #909399;
}

.stat-card .stat-label {
  color: #909399;
  margin-top: 5px;
}

.pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

.amount {
  color: #f56c6c;
  font-weight: bold;
}
</style>
